import { useMutation, useQuery } from "urql";
import { graphql } from "../../../__generated__/gql";
import { createColumnHelper, PaginationState } from "@tanstack/react-table";
import FormattedDate from "../../../ui/FormattedDate/FormattedDate";
import Table from "../../../ui/Table/Table";
import Button from "../../../ui/Button/Button";
import { useEffect, useState } from "react";
import styles from "./AdminTeams.module.scss";
import { Search } from "../../../ui/Icon/Icon";
import TextInput from "../../../ui/TextInput/TextInput";
import { BillingTier } from "../../../__generated__/gql/graphql";
import toSentenceCase from "../../../util/toSentenceCase";
import { useClerk } from "@clerk/clerk-react";
import urls from "../../../routes";
import useUser from "../../../providers/UserProvider/useUser";
import Truncate from "../../../ui/Truncate/Truncate";

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

const AdminTenantsQuery = graphql(/* GraphQL */ `
  query adminTenants(
    $query: String
    $paginationOptions: OffsetPaginationOptions
  ) {
    adminTenants(query: $query, paginationOptions: $paginationOptions) {
      totalCount
      nodes {
        id
        name
        createdAt
        updatedAt
        memberCount
        billingTier
        identityId
        joined
        owner {
          id
          email
        }
      }
    }
  }
`);

const AdminJoinTenantMutation = graphql(/* GraphQL */ `
  mutation adminJoinTenant($id: String!) {
    adminJoinTenant(id: $id) {
      id
      name
      createdAt
      updatedAt
      memberCount
      billingTier
      identityId
      joined
    }
  }
`);

const AdminLeaveTenantMutation = graphql(/* GraphQL */ `
  mutation adminLeaveTenant($id: String!) {
    adminLeaveTenant(id: $id) {
      id
      name
      createdAt
      updatedAt
      memberCount
      billingTier
      identityId
      joined
    }
  }
`);

const AdminDeleteTenantMutation = graphql(/* GraphQL */ `
  mutation adminDeleteTenant($id: String!) {
    adminDeleteTenant(id: $id) {
      id
    }
  }
`);

type AdminTenantRow = {
  id: string;
  identityId: string;
  name?: string | null;
  owner?: { id: string; email?: string | null } | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  memberCount?: number | null;
  billingTier?: BillingTier | null;
  joined?: boolean | null;
};

const columnHelper = createColumnHelper<AdminTenantRow>();

function AdminTeams() {
  const { setActive } = useClerk();

  const { tenant } = useUser();

  const [, adminJoinTenant] = useMutation(AdminJoinTenantMutation);
  const [, adminLeaveTenant] = useMutation(AdminLeaveTenantMutation);
  const [, adminDeleteTenant] = useMutation(AdminDeleteTenantMutation);

  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [keyPhrase, setKeyPhrase] = useState<string>("");
  const [debouncedKeyPhrase, setDebouncedKeyPhrase] = useState<string>("");

  const [adminTenantsResponse] = useQuery({
    query: AdminTenantsQuery,
    variables: {
      query: debouncedKeyPhrase.length > 2 ? debouncedKeyPhrase : undefined,
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
    },
  });

  const handleJoinTeam = async (id: string) => {
    await adminJoinTenant({ id });
  };

  const handleLeaveTeam = async (id: string) => {
    await adminLeaveTenant({ id });
  };

  const handleDeleteTeam = async (id: string) => {
    await adminDeleteTenant({ id });
  };

  const columns = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: ({ cell }) => cell.getValue(),
    }),
    columnHelper.accessor("billingTier", {
      header: "Billing Tier",
      cell: ({ cell }) => {
        const value = cell.getValue();

        return value ? toSentenceCase(value) : "-";
      },
    }),
    columnHelper.accessor("owner", {
      header: "Owner",
      cell: ({ cell }) => <Truncate>{cell.getValue()?.email ?? "-"}</Truncate>,
    }),
    columnHelper.accessor("memberCount", {
      header: "Members",
      cell: ({ cell }) => cell.getValue(),
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      cell: ({ cell }) => {
        const value = cell.getValue();

        return value ? <FormattedDate date={value} /> : "-";
      },
    }),
    columnHelper.accessor("updatedAt", {
      header: "Updated",
      cell: ({ cell }) => {
        const value = cell.getValue();

        return value ? <FormattedDate date={value} /> : "-";
      },
    }),
    columnHelper.display({
      id: "Actions",
      size: 267,
      cell: ({ row }) => (
        <div className={styles.actions}>
          {row.original.joined ? (
            <>
              <Button
                variant="primary"
                disabled={tenant?.identityId === row.original.identityId}
                onClick={async () => {
                  localStorage.setItem(
                    "previousOrganizationId",
                    row.original.identityId ?? "",
                  );

                  await setActive({
                    organization: row.original.identityId,
                  });

                  window.location.href = urls.home;
                }}
              >
                Log in
              </Button>
              <Button
                variant="primary"
                disabled={
                  tenant?.identityId === row.original.identityId ||
                  row.original.memberCount === 1
                }
                onClick={() => handleLeaveTeam(row.original.id)}
              >
                Leave
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={() => handleJoinTeam(row.original.id)}
            >
              Join
            </Button>
          )}
          <Button
            variant="primary"
            destructive
            disabled={tenant?.identityId === row.original.identityId}
            onClick={async () => {
              const teamName = window.prompt(
                "Enter the name of the team to delete",
              );

              if (!teamName) {
                return;
              }

              if (teamName !== row.original.name) {
                alert("Team name does not match");
                return;
              }

              await handleDeleteTeam(row.original.id);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    }),
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(DEFAULT_PAGINATION_STATE);
      setDebouncedKeyPhrase(keyPhrase);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyPhrase]);

  return (
    <div className={styles.container}>
      <Table
        data={adminTenantsResponse.data?.adminTenants.nodes ?? []}
        loading={adminTenantsResponse.fetching}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={adminTenantsResponse.data?.adminTenants.totalCount}
        resourceName={{ plural: "teams", singular: "team" }}
        Header={
          <div className={styles.header}>
            <TextInput
              leftSection={<Search size="medium" />}
              onChange={(event) => {
                setKeyPhrase(event.currentTarget.value);
              }}
              placeholder="Search teams"
              value={keyPhrase}
              className={styles.search}
            />
          </div>
        }
      />
    </div>
  );
}

export default AdminTeams;
